import {
  PaymentCollectionOptions,
  PaymentCollectionType
} from '@features/group-management/constants';
import { BranchTag } from '@features/member-management/queries/MemberQueryTypes';
import { UserProfileCustomField } from '@features/member-management/types';
import { toUiTag } from '@helpers/tags';
import { AddMemberInput, GroupFeeConfigurations, TermData } from '@types';
import { formatISODate } from '@utils/date';
import { roundTo2 } from '@utils/generic';
import { snakeToCamel } from '@utils/stringFormat';
import { Tag } from 'types/lead-management';

import { parseDateString } from '../helper';

// eslint-disable-next-line import/prefer-default-export
export const getFeeCategoryData = (
  feeCategories: string[],
  groupFeeConfigurations: GroupFeeConfigurations[],
  member: AddMemberInput
) => {
  const feeConfigData: any[] = [];
  feeCategories.forEach((category) => {
    const amount = member[category as keyof AddMemberInput];
    const categoryItem = groupFeeConfigurations?.find(
      (item) => item.category === category
    );

    if (categoryItem) {
      const rawValue =
        amount !== null && amount !== undefined && amount !== ''
          ? amount
          : categoryItem.amount;
      feeConfigData.push({
        id: categoryItem.branch_fee_category_id ?? categoryItem.id,
        label: categoryItem.category,
        value:
          rawValue !== null && rawValue !== undefined && rawValue !== ''
            ? roundTo2(Number(rawValue))
            : rawValue
      });
    }
  });
  let totalAmount = 0;
  feeConfigData.forEach((category) => {
    if (
      category.value !== null &&
      category.value !== undefined &&
      category.value !== ''
    )
      totalAmount += Number(category.value);
  });
  const hasExplicitValues = feeConfigData.some(
    (c) => c.value !== null && c.value !== undefined && c.value !== ''
  );
  return {
    feeConfigData,
    totalAmount: hasExplicitValues ? `${roundTo2(totalAmount)}` : ''
  };
};

const formattedDate = (value: string) => {
  const { date, error } = parseDateString(value);
  if (error) return '';
  if (date)
    return formatISODate({
      date,
      separator: '-',
      format: 'yyyy-mm-dd'
    });
  return '';
};

const AMOUNT_FIELDS = new Set([
  'amount',
  'amountPaid',
  'ratePerClass',
  'courseFee',
  'totalFee',
  'discountAmount'
]);

export const sanitizeMemberInfo = (
  memberInfo: AddMemberInput[],
  feeCategories: string[] = [],
  termsName: string[] = []
) => {
  const amountKeys = new Set<string>(AMOUNT_FIELDS);
  feeCategories.forEach((cat) => amountKeys.add(cat));
  termsName.forEach((term) => {
    amountKeys.add(`${term}_Amount`);
    feeCategories.forEach((cat) => {
      amountKeys.add(`${term}_${cat.replace(/\s+/g, '')}`);
    });
  });

  memberInfo.forEach((member) => {
    Object.keys(member).forEach((key) => {
      const value = member[key as keyof AddMemberInput];
      if (typeof value === 'string') {
        member[key as keyof AddMemberInput] = value.trim() as any;
      }
      if (amountKeys.has(key) && value !== null && value !== undefined) {
        const numVal = Number(value);
        if (!Number.isNaN(numVal) && `${value}`.trim() !== '') {
          (member as any)[key] = roundTo2(numVal);
        }
      }
    });
  });
};

export const processMemberInfo = (
  memberInfo: AddMemberInput[],
  feeCategories: string[],
  groupFrequency: string | undefined,
  groupIntervalFrequency: number | undefined,
  groupFeeConfigurations: GroupFeeConfigurations[] | undefined,
  termsName: string[] = []
) => {
  let hasPrimaryNumberError = false;

  memberInfo.forEach((member: AddMemberInput) => {
    if (
      `${member.primaryNumber}`.endsWith('00000') ||
      `${member.altNumber}`.endsWith('00000')
    ) {
      hasPrimaryNumberError = true;
    }
    if (feeCategories.length > 0) {
      if (feeCategories.length > 0 && groupFeeConfigurations) {
        const { feeConfigData, totalAmount } = getFeeCategoryData(
          feeCategories,
          groupFeeConfigurations,
          member
        );
        member.feeCategoryData = feeConfigData;
        member.amount = totalAmount;
      }
    } else if (`${member.amount}` === '0') {
      member.amount = '0';
    } else if (member.amount === null) {
      member.amount = '';
    }
    if (member.startDate !== null) {
      member.startDate = formattedDate(member.startDate || '');

      if (!member.frequency) {
        member.frequency = PaymentCollectionOptions.find(
          (option) => option.value === groupFrequency
        )?.label;
        // take group's frequency
      }
      if (member.frequency !== 'By Term/Installments') {
        member.monthsPerTerm = '1';
      }
      if (!member.monthsPerTerm) {
        member.monthsPerTerm = `${groupIntervalFrequency}`;
      }
    }
    if (member.endDate) {
      member.endDate = formattedDate(member.endDate);
    }
    if (member.dateOfBirth) {
      member.dateOfBirth = formattedDate(member.dateOfBirth);
    }
    if (member.admissionDate) {
      member.admissionDate = formattedDate(member.admissionDate);
    }
    termsName.forEach((term) => {
      const dueDateKey = `${term}_duedate` as keyof AddMemberInput;
      const dueDateValue = member[dueDateKey];
      if (dueDateValue) {
        (member as any)[dueDateKey] = formattedDate(String(dueDateValue));
      }
    });
  });
  return hasPrimaryNumberError;
};

const calculateFeeInfoCategory = (
  termsName: string[],
  termsData: TermData[] | undefined,
  member: AddMemberInput
) => {
  const feesInfoCategory: Record<string, Record<string, string>> = {};
  let totalAmountForFeeCategory = 0;

  termsName.forEach((term, termIndex) => {
    feesInfoCategory[(term?.toLowerCase() || '').replace(' ', '')] = {};
    let termTotalAmount = 0;

    member.feeCategoryData?.forEach((category) => {
      const feeKey = `${term}_${category.label
        ?.toString()
        .replaceAll(' ', '')}`;
      const termData = termsData?.[termIndex];

      // Find the corresponding fee category in termData
      const feeCategory = termData?.fee_split.find(
        (fee) =>
          ((fee.category as string)?.toLowerCase() || '') ===
          ((category.label as string)?.toLowerCase() || '')
      );

      const memberValue = member[feeKey as keyof AddMemberInput];
      const rawAmount =
        memberValue?.toString() || feeCategory?.amount?.toString() || '';
      const categoryAmount =
        rawAmount !== '' ? roundTo2(Number(rawAmount)).toString() : rawAmount;

      feesInfoCategory[(term?.toLowerCase() || '').replace(' ', '')][
        (category.label as string)?.toLowerCase() || ''
      ] = categoryAmount;

      termTotalAmount += Number(categoryAmount) || 0;
    });

    totalAmountForFeeCategory += roundTo2(termTotalAmount);
  });
  return { feesInfoCategory, totalAmountForFeeCategory };
};

const calculateFeeInfo = (
  termsData: TermData[] | undefined,
  member: AddMemberInput,
  defaultTermLabel?: string
) => {
  const prefix = defaultTermLabel || 'Installment';
  const feesInfo: Record<string, string> = {};
  let totalAmountWithoutFeeCategory = 0;
  termsData?.forEach((term) => {
    const termName = term.label || `${prefix} ${term.term_number}`;
    const termKey = `${termName}_Amount`;
    const memberValue = member[termKey as keyof AddMemberInput];

    // If member has a value for this term, use it;
    // otherwise use termsData value
    if (memberValue || (typeof memberValue === 'number' && memberValue === 0)) {
      const rounded = roundTo2(Number(memberValue));
      feesInfo[`term${term.term_number}`] = rounded.toString();
      totalAmountWithoutFeeCategory += rounded;
    } else {
      feesInfo[`term${term.term_number}`] = term.fee_amount.toString();
      totalAmountWithoutFeeCategory += Number(term.fee_amount);
    }
  });
  return { feesInfo, totalAmountWithoutFeeCategory };
};

const formatFeesInfo = (
  feesInfo: Record<string, string>,
  termsData: TermData[] | undefined,
  member?: AddMemberInput,
  defaultTermLabel?: string
) =>
  Object.keys(feesInfo).map((termKey) => {
    const termNumber = parseInt(termKey.replace('term', ''), 10);
    const termData = termsData?.find((term) => term.term_number === termNumber);
    const prefix = defaultTermLabel || 'Installment';
    const termName = termData?.label || `${prefix} ${termNumber}`;
    const memberDueDate = member
      ? (member[`${termName}_duedate` as keyof AddMemberInput] as string)
      : undefined;

    return {
      id: termNumber,
      term_label: termData?.label || termKey,
      dueDate: memberDueDate || termData?.due_date || '',
      dueAmount:
        feesInfo[termKey]?.toString() || termData?.fee_amount.toString()
    };
  });

const formatFeesInfoCategory = (
  feesInfoCategory: Record<string, Record<string, string>>,
  termsData: TermData[] | undefined,
  termsName?: string[],
  member?: AddMemberInput
) =>
  Object.keys(feesInfoCategory).map((termKey, index) => {
    const termData = termsData?.[index];
    const termLabel = termsName?.[index];
    const memberDueDate =
      member && termLabel
        ? (member[`${termLabel}_duedate` as keyof AddMemberInput] as string)
        : undefined;

    // Calculate due amount as the sum of the fee category values
    const dueAmount = roundTo2(
      Object.values(feesInfoCategory[termKey]).reduce(
        (sum, value) => sum + Number(value),
        0
      )
    ).toString();
    // Construct feeCategoryData array
    const feeCategoryData = Object.keys(feesInfoCategory[termKey]).map(
      (categoryName) => {
        const feeCategory = termData?.fee_split.find(
          (fee) =>
            (fee.category?.toLowerCase() || '') ===
            (categoryName?.toLowerCase() || '')
        );
        const rawAmt =
          feesInfoCategory[termKey][categoryName] || feeCategory?.amount;
        return {
          branch_fee_category_id: feeCategory?.branch_fee_category_id || null,
          account_detail_id: feeCategory?.account_detail_id || null,
          category: categoryName,
          amount:
            rawAmt !== null && rawAmt !== undefined && rawAmt !== ''
              ? roundTo2(Number(rawAmt))
              : rawAmt
        };
      }
    );

    return {
      id: index + 1,
      term_label: termData?.label || termKey,
      dueDate: memberDueDate || termData?.due_date || '',
      dueAmount,
      feeCategoryData
    };
  });

export const buildFormattedMemberData = (
  member: AddMemberInput,
  termsName: string[],
  termsData: TermData[] | undefined,
  feeCategories: string[],
  isCustomTerm: boolean | undefined,
  groupStartDate: string | undefined,
  isLeadSquared: boolean,
  isEndDateEnabled: boolean,
  isAttendanceBasedFee?: boolean,
  isSessionBased?: boolean,
  defaultTermLabel?: string,
  orgCustomFields: UserProfileCustomField[] = []
) => {
  const { feesInfoCategory, totalAmountForFeeCategory } =
    calculateFeeInfoCategory(termsName, termsData, member);
  const { feesInfo, totalAmountWithoutFeeCategory } = calculateFeeInfo(
    termsData,
    member,
    defaultTermLabel
  );
  const formattedFeesInfo = formatFeesInfo(
    feesInfo,
    termsData,
    member,
    defaultTermLabel
  );
  const formattedFeesInfoCategory = formatFeesInfoCategory(
    feesInfoCategory,
    termsData,
    termsName,
    member
  );

  const getTotalFee = () => {
    if (isLeadSquared) {
      if (member.totalFee) return `${roundTo2(Number(member.totalFee))}`;
      if (member.courseFee) return `${roundTo2(Number(member.courseFee))}`;
      return '';
    }
    return '';
  };

  return {
    altNumber: member.altNumber ? `${member.altNumber}` : '',
    // eslint-disable-next-line no-nested-ternary
    amount: isCustomTerm
      ? feeCategories.length > 0
        ? roundTo2(totalAmountForFeeCategory).toString()
        : roundTo2(totalAmountWithoutFeeCategory).toString()
      : member.amount !== null &&
        member.amount !== undefined &&
        member.amount !== ''
      ? `${roundTo2(Number(member.amount))}`
      : '',
    frequency: member.frequency || '',
    monthsPerTerm: member.monthsPerTerm || '',
    startDate: isCustomTerm ? groupStartDate : member.startDate || '',
    endDate: isEndDateEnabled ? member.endDate || '' : '',
    totalFee: getTotalFee(),
    name: member.name || '',
    primaryNumber: `${member.primaryNumber}` || '',
    primaryNumberCountryCode: `${member.primaryNumberCode}` || '',
    altNumberCountryCode: member.altNumberCode ? `${member.altNumberCode}` : '',
    email: member.email ?? '',
    ...(!isCustomTerm && { feeCategoryData: member.feeCategoryData }),
    amountPaid: member.amountPaid
      ? `${roundTo2(Number(member.amountPaid))}`
      : '',
    admissionNumber: member.admissionNumber?.toString() || '',
    admissionDate: member.admissionDate || '',
    dateOfBirth: member.dateOfBirth || '',
    guardianName: member.guardianName || '',
    addressLine1: member.addressLine1 || null,
    addressLine2: member.addressLine2 || null,
    country: member.country || null,
    state: member.state || null,
    city: member.city || null,
    pincode: member.pincode?.toString() || null,
    age: member.age || '',
    ...orgCustomFields.reduce((acc, field) => {
      const key = snakeToCamel(field.key);
      const val = (member as Record<string, any>)[field.label];
      if (val !== undefined) acc[key] = val;
      return acc;
    }, {} as Record<string, any>),
    ...(isCustomTerm && {
      termsList:
        feeCategories.length > 0 ? formattedFeesInfoCategory : formattedFeesInfo
    }),
    ...(isCustomTerm &&
      member.discountLabel?.trim() &&
      Number(member.discountAmount) > 0 && {
        discount: [
          {
            label: member.discountLabel.trim(),
            type: 'flat',
            amount: String(roundTo2(Number(member.discountAmount)))
          }
        ]
      }),
    ...(isAttendanceBasedFee && {
      paymentCollectionType: isSessionBased
        ? PaymentCollectionType.SESSION_BASED
        : PaymentCollectionType.MONTHLY,
      interval: '1',
      ...(isSessionBased
        ? { sessionsPerCycle: (member as any).sessionsPerCycle || 0 }
        : { startDate: member.startDate || '' })
    })
  };
};

export const buildFeesInfo = (
  row: AddMemberInput,
  termsData: TermData[] | undefined,
  defaultTermLabel?: string
) => {
  const prefix = defaultTermLabel || 'Installment';
  const feesInfo: Record<string, string> = {};
  termsData?.forEach((term) => {
    const termName = term.label || `${prefix} ${term.term_number}`;
    const termKey = `${termName}_Amount`;
    const memberValue = row[termKey as keyof AddMemberInput];

    if (memberValue || (typeof memberValue === 'number' && memberValue === 0)) {
      feesInfo[`${termName}_Amount`] = roundTo2(Number(memberValue)).toString();
    } else {
      feesInfo[`${termName}_Amount`] = term.fee_amount.toString();
    }
  });
  return feesInfo;
};

export const buildFeesInfoCategoryAndFlatStructure = (
  row: AddMemberInput,
  termsData: TermData[] | undefined,
  termsName: string[]
) => {
  const feesInfoCategory: Record<string, Record<string, string>> = {};
  const formattedFeesInfo: Record<string, string> = {};
  termsName.forEach((term, termIndex) => {
    row.feeCategoryData?.forEach((category) => {
      const feeKey = `${term}_${category.label.toString().replaceAll(' ', '')}`;
      const termData = termsData?.[termIndex];

      const feeCategory = termData?.fee_split.find(
        (fee) =>
          ((fee.category as string)?.toLowerCase() || '') ===
          ((category.label as string)?.toLowerCase() || '')
      );

      const memberValue = row[feeKey as keyof AddMemberInput];
      const rawAmt =
        memberValue?.toString() || feeCategory?.amount?.toString() || '';
      const categoryAmount =
        rawAmt !== '' ? roundTo2(Number(rawAmt)).toString() : rawAmt;

      feesInfoCategory[
        typeof term === 'string' ? term.toLowerCase().replace(' ', '') : term
      ] ||= {};

      feesInfoCategory[
        typeof term === 'string' ? term.toLowerCase().replace(' ', '') : term
      ][
        typeof category.label === 'string'
          ? category.label.toLowerCase()
          : category.label
      ] = categoryAmount;

      // Fill flat structure
      formattedFeesInfo[feeKey] = categoryAmount;
    });
  });
  return { feesInfoCategory, formattedFeesInfo };
};

export const resolveImportedTags = (
  memberInfo: AddMemberInput[],
  tagOptions: BranchTag[]
) => {
  const tagsByRow: Tag[][] = [];
  const tagErrors: string[] = [];
  const tagsByName = new Map(
    tagOptions.map((tag) => [tag.name.trim().toLowerCase(), tag])
  );

  memberInfo.forEach((member, index) => {
    const rawTags = `${member.tags ?? ''}`.trim();
    if (!rawTags) {
      tagsByRow.push([]);
      return;
    }

    const rowTags: Tag[] = [];
    rawTags
      .split(',')
      .map((name) => name.trim())
      .filter(Boolean)
      .forEach((name) => {
        const option = tagsByName.get(name.toLowerCase());
        if (!option) {
          tagErrors.push(`Row ${index + 1}: Tag "${name}" not in the branch`);
          return;
        }
        const uiTag = toUiTag(option);
        if (!rowTags.some((tag) => tag.id === uiTag.id)) {
          rowTags.push(uiTag);
        }
      });
    tagsByRow.push(rowTags);
  });

  return { tagsByRow, tagErrors };
};
